import {
  forwardRef, useRef, useState,
  type CSSProperties, type HTMLAttributes, type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

/** How far a row has to travel before letting go commits it. Below this it
 *  springs back, which is what makes an accidental horizontal nudge harmless. */
const THRESHOLD = 88

/** Movement before the gesture is claimed at all, and the margin by which
 *  vertical has to beat horizontal to be a scroll instead. Without the second
 *  test every attempt to scroll the list starts a swipe. */
const SLOP = 12

/** A swipe ends in a click the browser sends anyway. Without swallowing it,
 *  every swipe also fires `onPress`. */
const SWALLOW_MS = 400

/** Long enough that a scroll flick is not a long press, short enough to feel
 *  deliberate. */
const LONG_PRESS_MS = 450

/** The leave animation's length. **Must match `--dur` in tokens.css** — a
 *  timeout cannot read a custom property, and the action has to fire when the
 *  row has finished going rather than while it is still on screen. Not driven
 *  off `transitionend`: under `prefers-reduced-motion: reduce` there is no
 *  transition, so no event, and the action would never fire at all. */
export const LEAVE_MS = 200

export interface SwipeAction {
  /** What the backdrop says while the row is being dragged. */
  label: string
  /** Run once the row has passed the threshold and been let go. */
  onAction: () => void
  /** The row goes away. It flies out and collapses its height, and
   *  `onAction` fires when that has finished — so a list that removes the row
   *  does it after the gap has already closed rather than snapping it shut.
   *
   *  Default false, which is the safe half: the row springs back and
   *  `onAction` fires at once. A caller that sets this and then does *not*
   *  remove the row leaves a row flown off its own edge, so the flag means
   *  "this row is about to be unmounted", not "animate please". */
  leaves?: boolean
}

export interface RowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Tasks plan D1: the parent says this row is on its way out — the same
   *  collapse the swipe's `leaves` does, with no slide, for a row that was
   *  ticked rather than swiped. The parent unmounts it when the transition
   *  has run (`LINGER_MS` + `LEAVE_MS`); a row left `leaving` is a row that
   *  stays collapsed, so the prop means "about to be unmounted". */
  leaving?: boolean
  /** The 56px slot: a square thumbnail, a 2:3 poster, a time plate, an icon.
   *  Required, and `null` is a legitimate answer — a row with an empty leading
   *  slot still has the slot, which is what keeps a list of mixed rows in one
   *  column. `ui.css` reads the width from `--u-lead`; `u-row--list` sets it
   *  to the 44px a plain list of settings wants. */
  leading: ReactNode
  title: ReactNode
  /** The second line. `null` for a one-line row. */
  meta: ReactNode
  /** The 44px trailing slot — a chevron, a heart, a checkbox. It sits outside
   *  the press target rather than inside it, so a control here is a control
   *  and not a button nested in a button, and a swipe never starts on it. */
  trailing?: ReactNode
  /** Makes the title-and-meta area a real button. Omitted, the row is inert. */
  onPress?: () => void
  /** a2-34 — this row opens something: a chevron at its end, *inside* the
   *  press target, so the whole row including the chevron is the tap. The
   *  `trailing` slot is the wrong place for it: that slot sits outside the
   *  button on purpose (a control there is a control), which would leave a
   *  44px dead end exactly where the chevron invites the tap. `'down'` for
   *  a row whose content is open beneath it. Ignored without `onPress`. */
  chevron?: 'right' | 'down'
  /** Keyed by the direction of the drag, not by the edge the label appears on:
   *  `left` is what a leftward drag commits (removal, today), `right` what a
   *  rightward one commits (saving). The backdrop label shows on the opposite
   *  edge — the one the row uncovers as it moves — which is why these two are
   *  named after the gesture and `ui.css` names its two backdrop slots after
   *  the edges. One word meaning both a direction and a position is how this
   *  codebase writes its recurring bug. */
  swipe?: { left?: SwipeAction; right?: SwipeAction }
  /** 450ms. Opens whatever the caller renders; the trailing click is swallowed
   *  so the press does not also fire. */
  onLongPress?: () => void
  /** The native state of the press target. Not a variant: a row with no
   *  `onPress` is inert, a disabled row is one that would normally answer and
   *  cannot right now. */
  disabled?: boolean
}

/**
 * The one row anatomy: a leading slot, two lines, a trailing slot, and the
 * gestures. Every list in the app is made of these.
 *
 * The swipe is `SwipeRow.tsx`'s mechanics, with `SwipeItem.tsx`'s pointer
 * capture — which `SwipeRow` never had, and which is why a drag there dies the
 * moment the pointer leaves the row. Capture is claimed when the drag is
 * *confirmed*, not on pointerdown: claiming it early retargets pointerup to
 * this element, the browser then finds pointerdown and pointerup on different
 * elements and synthesises no click at all, and a plain desktop click on the
 * row does nothing (UI-audit F10).
 */
export const Row = forwardRef<HTMLDivElement, RowProps>(function Row(
  { leading, title, meta, trailing, onPress, chevron, swipe, onLongPress, disabled, leaving: leavingProp,
    className = '', style, ...rest },
  ref,
) {
  const [dx, setDx] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [leaving, setLeaving] = useState<'l' | 'r' | null>(null)
  const start = useRef<{ x: number; y: number; on: boolean } | null>(null)
  const swallow = useRef(false)
  const longPress = useRef<ReturnType<typeof setTimeout> | null>(null)

  function cancelLongPress() {
    if (longPress.current === null) return
    clearTimeout(longPress.current)
    longPress.current = null
  }

  /** Eat the click the browser sends at the end of a gesture, for a while. */
  function armSwallow() {
    swallow.current = true
    setTimeout(() => { swallow.current = false }, SWALLOW_MS)
  }

  function down(ev: ReactPointerEvent) {
    // Only the primary pointer: a second finger landing mid-drag used to reset
    // the origin, so the row jumped.
    if (!ev.isPrimary) return
    // The trailing slot is the one place a drag must not start — everything in
    // it is a control. Excluding every `button` instead would leave almost
    // nowhere to start a swipe, since the press target covers the rest of the
    // row (UI-audit F01).
    if ((ev.target as HTMLElement).closest('.u-row__trail')) return
    start.current = { x: ev.clientX, y: ev.clientY, on: false }

    if (onLongPress) {
      longPress.current = setTimeout(() => {
        longPress.current = null
        start.current = null
        armSwallow()
        onLongPress()
      }, LONG_PRESS_MS)
    }
  }

  function move(ev: ReactPointerEvent) {
    const s = start.current
    if (!s) return
    const x = ev.clientX - s.x
    const y = ev.clientY - s.y
    if (!s.on) {
      if (Math.abs(x) > SLOP || Math.abs(y) > SLOP) cancelLongPress()
      if (Math.abs(y) > SLOP && Math.abs(y) > Math.abs(x)) { start.current = null; return }
      if (Math.abs(x) < SLOP) return
      if (!swipe) { start.current = null; return }
      s.on = true
      setDragging(true)
      try { (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId) }
      catch { /* Safari throws if the pointer is already gone. */ }
    }
    // Clamped to the directions that have an action. A drag toward nothing
    // would otherwise open a backdrop with no label and spring back.
    setDx(x > 0 ? (swipe?.right ? x : 0) : (swipe?.left ? x : 0))
  }

  function up(ev: ReactPointerEvent) {
    const s = start.current
    start.current = null
    cancelLongPress()
    try { (ev.currentTarget as HTMLElement).releasePointerCapture(ev.pointerId) }
    catch { /* already released */ }
    if (!s?.on) return

    setDragging(false)
    armSwallow()

    const action = dx > THRESHOLD ? swipe?.right : dx < -THRESHOLD ? swipe?.left : undefined
    if (!action) { setDx(0); return }
    if (!action.leaves) { setDx(0); action.onAction(); return }
    setLeaving(dx > 0 ? 'r' : 'l')
    setTimeout(action.onAction, LEAVE_MS)
  }

  const inner = (
    <>
      <span className="u-row__lead">{leading}</span>
      <span className="u-row__body">
        <span className="u-row__title">{title}</span>
        {meta != null && meta !== false && <span className="u-row__meta">{meta}</span>}
      </span>
      {onPress && chevron && (
        <span className="u-row__chev" aria-hidden="true">
          {chevron === 'down' ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </span>
      )}
    </>
  )

  const classes = [
    'u-row',
    dragging ? 'is-dragging' : '',
    leaving ? `is-leaving is-leaving-${leaving}` : leavingProp ? 'is-leaving' : '',
    onLongPress ? 'u-row--longpress' : '',
    onPress && chevron ? 'u-row--chev' : '',
    className,
  ].filter(Boolean).join(' ')

  return (
    <div
      ref={ref}
      className={classes}
      style={dx ? ({ ...style, '--u-dx': `${dx}px` } as CSSProperties) : style}
      onPointerDown={swipe || onLongPress ? down : undefined}
      onPointerMove={swipe || onLongPress ? move : undefined}
      onPointerUp={swipe || onLongPress ? up : undefined}
      onPointerCancel={swipe || onLongPress ? up : undefined}
      onClickCapture={ev => {
        if (!swallow.current) return
        swallow.current = false
        ev.stopPropagation()
        ev.preventDefault()
      }}
      {...rest}
    >
      {swipe && (
        // Named after the edges, because that is where they are drawn: the
        // `start` label is uncovered by a rightward drag and so belongs to
        // `swipe.right`.
        <div className="u-row__bg" aria-hidden="true">
          <span className="u-row__bg-start">{swipe.right?.label}</span>
          <span className="u-row__bg-end">{swipe.left?.label}</span>
        </div>
      )}

      <div className="u-row__slide">
        {onPress ? (
          <button type="button" className="u-row__main" onClick={onPress} disabled={disabled}>
            {inner}
          </button>
        ) : (
          <div className="u-row__main">{inner}</div>
        )}
        {trailing != null && trailing !== false && <div className="u-row__trail">{trailing}</div>}
      </div>
    </div>
  )
})
